"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Locale } from "@/lib/i18n";

type LanguageContextType = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  isLoading: boolean;
};

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

const STORAGE_KEY = "merchant-portal-locale";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("en");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load saved locale from localStorage on mount
    const savedLocale = localStorage.getItem(STORAGE_KEY);
    if (
      savedLocale &&
      (savedLocale === "en" || savedLocale === "my" || savedLocale === "th")
    ) {
      setLocaleState(savedLocale as Locale);
    }
    setIsLoading(false);
  }, []);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem(STORAGE_KEY, newLocale);
  };

  return (
    <LanguageContext.Provider value={{ locale, setLocale, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

// Helper hook to get translations
export function useTranslations() {
  const { locale } = useLanguage();

  // This is a simplified version - in production, you'd use next-intl's useTranslations
  return {
     
    t: (key: string, _params?: Record<string, string | number>) => {
      // This will be replaced with actual next-intl usage
      return key;
    },
    locale,
  };
}
